"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleDashed } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  year: "2025",
  title: "Reducción de Carbono",
  description: "Implementación de sistemas de filtrado de alta eficiencia en todas nuestras plantas de acero para reducir las emisiones de CO2 en un 30% respecto al año base.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/carbon_reduction_steel.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/carbon_reduction_steel.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/carbon_reduction_steel.png-wvc-sizes",
    alt: "Planta industrial con sistemas de filtrado"
  }
}, {
  year: "2027",
  title: "Energía 100% Renovable",
  description: "Transición completa de nuestra matriz energética hacia fuentes solares y eólicas, garantizando que cada tonelada de acero se produzca con energía limpia.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/renewable_energy_solar.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/renewable_energy_solar.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/renewable_energy_solar.png-wvc-sizes",
    alt: "Paneles solares en complejo industrial"
  }
}, {
  year: "2029",
  title: "Economía Circular Total",
  description: "Optimización de nuestros procesos de reciclaje para lograr residuo cero, reintegrando el 100% de los subproductos industriales en la cadena de valor.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/circular_economy_waste.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/circular_economy_waste.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/circular_economy_waste.png-wvc-sizes",
    alt: "Proceso de reciclaje de chatarra"
  }
}, {
  year: "2030",
  title: "Liderazgo en Acero Verde",
  description: "Consolidación como referentes en la industria siderúrgica sostenible, cumpliendo con los más altos estándares internacionales de protección ambiental.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/green_steel_future.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/green_steel_future.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/green_steel_future.png-wvc-sizes",
    alt: "Acero sostenible de alta calidad"
  }
}];
function TimelineItem({
  milestone,
  index
}: {
  milestone: (typeof milestones)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("relative flex items-center justify-between w-full mb-16 md:mb-24 last:mb-0 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      {}
      <div className="absolute left-8 md:left-1/2 w-12 h-12 rounded-lg bg-primary border-4 border-background flex items-center justify-center -translate-x-1/2 z-10 shadow-md">
        <CircleDashed className="w-6 h-6 text-primary-foreground" />
      </div>

      {}
      <div className={cn("w-full flex", isEven ? "md:justify-start" : "md:justify-end")}>
        <div className={cn("w-full md:w-[45%] pl-24 md:pl-0", isEven ? "md:pr-16" : "md:pl-16")}>
          <Card className="overflow-hidden border-none shadow-lg shadow-primary/5 rounded-lg bg-card/90 backdrop-blur-sm transition-transform duration-500 hover:-translate-y-1">
            <AspectRatio ratio={16 / 10}>
              <img src={milestone.image.src} data-wvc-srcset={milestone.image.srcset} data-wvc-sizes={milestone.image.sizes} alt={milestone.image.alt} className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-700" loading="lazy" />
            </AspectRatio>
            <CardContent className="p-6 md:p-8">
              <span className="text-sm font-bold tracking-widest text-accent uppercase mb-3 block">
                {milestone.year}
              </span>
              <h3 className="text-2xl font-default font-bold mb-4 text-foreground">
                {milestone.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed font-default">
                {milestone.description}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}
export default function Environmentalcommitments() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="245893" id="environmentalcommitments" className="relative py-24 md:py-32 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-1000", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Compromisos Ambientales
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Nuestra hoja de ruta hacia una industria del acero sostenible. 
            Establecemos metas ambiciosas para proteger nuestro entorno y liderar el cambio hacia un futuro verde.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-4 bottom-4 w-0 border-l-2 border-dashed border-primary/30 -translate-x-1/2 z-0" />

          {}
          <div className="relative z-10">
            {milestones.map((milestone, i) => <TimelineItem key={i} milestone={milestone} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}